import { formatCurrency, formatDate } from '@/lib/formatters'
import {
  EffectiveBasis,
  PolicyChangeKind,
  type PolicyChangeLine,
  type PolicyChangeRow,
  PolicyChangeStatus,
  type PolicyChangeUnit,
  type PolicyChangeUnitEdit,
  RemovalReason,
} from '@/models/policyChange'

const Kind = PolicyChangeKind

export const KIND_GROUPS: { title: string; kinds: PolicyChangeKind[] }[] = [
  {
    title: 'Vehicles',
    kinds: [
      Kind.ADD_TRUCK,
      Kind.REMOVE_TRUCK,
      Kind.ADD_TRAILER,
      Kind.REMOVE_TRAILER,
      Kind.CHANGE_VEHICLE,
      Kind.OTHER_VEHICLE,
    ],
  },
  { title: 'Drivers', kinds: [Kind.ADD_DRIVER, Kind.REMOVE_DRIVER, Kind.CHANGE_DRIVER, Kind.OTHER_DRIVER] },
  {
    title: 'Coverage',
    kinds: [
      Kind.CHANGE_LIMIT,
      Kind.CHANGE_DEDUCTIBLE,
      Kind.ADD_COVERAGE,
      Kind.REMOVE_COVERAGE,
      Kind.CHANGE_OPERATIONS,
      Kind.OTHER_COVERAGE,
    ],
  },
  {
    title: 'Certificates and interests',
    kinds: [Kind.ADD_ADDITIONAL_INSURED, Kind.ADD_LOSS_PAYEE, Kind.WAIVER_OR_PRIMARY, Kind.REMOVE_INTEREST],
  },
  {
    title: 'Business details',
    kinds: [Kind.CHANGE_NAMED_INSURED, Kind.CHANGE_ADDRESS, Kind.CANCEL_OR_REINSTATE],
  },
]

export const KIND_LABELS: Record<PolicyChangeKind, string> = {
  [Kind.ADD_TRUCK]: 'Add a truck',
  [Kind.REMOVE_TRUCK]: 'Remove a truck',
  [Kind.ADD_TRAILER]: 'Add a trailer',
  [Kind.REMOVE_TRAILER]: 'Remove a trailer',
  [Kind.CHANGE_VEHICLE]: 'Change vehicle details',
  [Kind.OTHER_VEHICLE]: 'Something else about a vehicle',
  [Kind.ADD_DRIVER]: 'Add a driver',
  [Kind.REMOVE_DRIVER]: 'Remove a driver',
  [Kind.CHANGE_DRIVER]: 'Correct driver details',
  [Kind.OTHER_DRIVER]: 'Something else about a driver',
  [Kind.CHANGE_LIMIT]: 'Change a limit',
  [Kind.CHANGE_DEDUCTIBLE]: 'Change a deductible',
  [Kind.ADD_COVERAGE]: 'Add a coverage',
  [Kind.REMOVE_COVERAGE]: 'Remove a coverage',
  [Kind.CHANGE_OPERATIONS]: 'Change what or where you haul',
  [Kind.OTHER_COVERAGE]: 'Something else about coverage',
  [Kind.ADD_ADDITIONAL_INSURED]: 'Add an additional insured',
  [Kind.ADD_LOSS_PAYEE]: 'Add a loss payee or lienholder',
  [Kind.WAIVER_OR_PRIMARY]: 'Add a waiver of subrogation, or primary and non-contributory wording',
  [Kind.REMOVE_INTEREST]: 'Remove an additional insured or loss payee',
  [Kind.CHANGE_NAMED_INSURED]: 'Change the business name on your policy (named insured) or DBA',
  [Kind.CHANGE_ADDRESS]: 'Change your mailing or garaging address',
  [Kind.CANCEL_OR_REINSTATE]: 'Cancel or reinstate a policy',
  [Kind.OTHER]: 'Something else',
}

export interface DetailQuestion {
  key: string
  label: string
  optional?: boolean
  /** Asks about one of the customer's coverages, so it's picked from their policies. */
  fromPolicies?: boolean
  /** Answered from a list; the details keep the value, shown by its label. */
  choices?: { value: string; label: string }[]
  /** A US state, picked from the list. */
  state?: boolean
}

// Only a mailing address has a place in Cai, which updates it at the bind.
const ADDRESS_TYPES = [
  { value: 'MAILING', label: 'Mailing address' },
  { value: 'GARAGING', label: 'Garaging address' },
]

/** The questions a change with nothing on the roster asks; the answers are kept in its details. */
export const DETAIL_QUESTIONS: Partial<Record<PolicyChangeKind, DetailQuestion[]>> = {
  [Kind.CHANGE_LIMIT]: [
    { key: 'coverage', label: 'Which coverage', fromPolicies: true },
    { key: 'currentLimit', label: 'Current limit' },
    { key: 'requestedLimit', label: 'Limit you want' },
  ],
  [Kind.CHANGE_DEDUCTIBLE]: [
    { key: 'coverage', label: 'Which coverage', fromPolicies: true },
    { key: 'currentDeductible', label: 'Current deductible' },
    { key: 'requestedDeductible', label: 'Deductible you want' },
  ],
  [Kind.ADD_COVERAGE]: [
    { key: 'coverage', label: 'Which coverage' },
    { key: 'requestedLimit', label: 'Limit you want' },
  ],
  [Kind.REMOVE_COVERAGE]: [{ key: 'coverage', label: 'Which coverage', fromPolicies: true }],
  [Kind.CHANGE_OPERATIONS]: [
    { key: 'commodities', label: 'Commodities added or removed' },
    { key: 'radius', label: 'Radius or states you run in' },
  ],
  [Kind.ADD_ADDITIONAL_INSURED]: [
    { key: 'name', label: 'Name' },
    { key: 'address', label: 'Address' },
    { key: 'relationship', label: 'How you work with them' },
  ],
  [Kind.ADD_LOSS_PAYEE]: [
    { key: 'name', label: 'Lender name' },
    { key: 'address', label: 'Lender address' },
    { key: 'vehicles', label: 'Which vehicles' },
  ],
  [Kind.WAIVER_OR_PRIMARY]: [{ key: 'name', label: 'Who it is for' }],
  [Kind.REMOVE_INTEREST]: [{ key: 'name', label: 'Who to remove' }],
  [Kind.CHANGE_NAMED_INSURED]: [
    { key: 'legalName', label: 'New legal name' },
    { key: 'dba', label: 'New DBA (doing business as)', optional: true },
  ],
  [Kind.CHANGE_ADDRESS]: [
    { key: 'addressType', label: 'Which address', choices: ADDRESS_TYPES },
    { key: 'line1', label: 'Street address' },
    { key: 'line2', label: 'Suite or unit', optional: true },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State', state: true },
    { key: 'zip', label: 'ZIP code' },
  ],
  [Kind.CANCEL_OR_REINSTATE]: [
    { key: 'policy', label: 'Which policy' },
    { key: 'action', label: 'Cancel or reinstate' },
  ],
}

// What the add-a-vehicle form keeps in a change's details.
const ADD_VEHICLE_DETAILS: DetailQuestion[] = [
  { key: 'lossPayee', label: 'Loss payee' },
  { key: 'garagingAddress', label: 'Garaging address' },
]

/** "Current limit: $100,000" for each answer a change holds, and why a vehicle is leaving. */
export function detailLabels(line: PolicyChangeLine): string[] {
  const questions = DETAIL_QUESTIONS[line.kind] ?? ADD_VEHICLE_DETAILS
  const answers = questions
    .filter((question) => line.details[question.key])
    .map((question) => {
      const value = line.details[question.key]
      const choice = question.choices?.find((option) => option.value === value)
      return `${question.label}: ${choice?.label ?? value}`
    })
  const reason = REMOVAL_REASONS.find((option) => option.value === line.removalReason)
  return reason ? [...answers, `Reason: ${reason.label}`] : answers
}

const ADD_KINDS = [Kind.ADD_TRUCK, Kind.ADD_TRAILER, Kind.ADD_DRIVER]

/** What an addition puts on the roster beyond its name: a vehicle's value, a driver's license and dates. */
function addedLabels(line: PolicyChangeLine): string[] {
  const unit = line.unit
  if (!unit || !ADD_KINDS.includes(line.kind)) return []
  if (unit.type !== 'driver') return unit.price ? [`Value: ${formatCurrency(unit.price)}`] : []
  return [
    unit.licenseNumber && `License number: ${unit.licenseNumber}`,
    unit.dob && `Date of birth: ${formatDate(unit.dob)}`,
    unit.dateOfHire && `Hire date: ${formatDate(unit.dateOfHire)}`,
  ].filter((label): label is string => Boolean(label))
}

/** What a change says, one line each: the unit, the customer's words, their answers, then each edit. */
export function changeLines(line: PolicyChangeLine): string[] {
  return [
    unitLabel(line.unit),
    ...addedLabels(line),
    line.description,
    ...detailLabels(line),
    ...(line.unit?.edits ?? []).map(editLabel),
  ].filter(Boolean)
}

export const STATUS_LABELS: Record<PolicyChangeStatus, string> = {
  [PolicyChangeStatus.DRAFT]: 'Draft',
  [PolicyChangeStatus.SUBMITTED]: 'In review',
  [PolicyChangeStatus.NEEDS_INFO]: 'Needs your answer',
  [PolicyChangeStatus.WITH_CARRIER]: 'Sent to your insurance providers',
  [PolicyChangeStatus.QUOTE_READY]: 'Quote ready',
  [PolicyChangeStatus.ACCEPTED]: 'Accepted',
  [PolicyChangeStatus.BOUND]: 'In place',
  [PolicyChangeStatus.POLICY_UPDATED]: 'Policies updated',
  [PolicyChangeStatus.COMPLETE]: 'Complete',
  [PolicyChangeStatus.DECLINED]: 'Declined',
  [PolicyChangeStatus.CANCELED]: 'Canceled',
}

// A sent request the customer called off; a draft that never went out stays canceled.
export const WITHDRAWN_LABEL = 'Withdrawn'

export const BASIS_LABELS: Record<EffectiveBasis, string> = {
  [EffectiveBasis.REQUEST_DATE]: 'The day you asked',
  [EffectiveBasis.CHOSEN]: 'The date you chose',
  [EffectiveBasis.TERM_LETTER]: 'Termination letter date',
  [EffectiveBasis.LAST_LOAD]: 'Covered through the last load',
  [EffectiveBasis.ACTIVE_DATE]: 'Active date',
  [EffectiveBasis.FIRST_DRIVE]: 'First scheduled drive',
  [EffectiveBasis.BACKDATE_LIMIT]: 'Moved to today, the earliest date allowed',
  [EffectiveBasis.QUOTED]: 'The date your insurance providers quoted',
}

export const REMOVAL_REASONS: { value: RemovalReason; label: string }[] = [
  { value: RemovalReason.SOLD, label: 'Sold' },
  { value: RemovalReason.TOTAL_LOSS, label: 'Total loss' },
  { value: RemovalReason.DECOMMISSIONED, label: 'Decommissioned' },
  { value: RemovalReason.OTHER, label: 'Other' },
]

export const CLOSED_STATUSES = [
  PolicyChangeStatus.COMPLETE,
  PolicyChangeStatus.DECLINED,
  PolicyChangeStatus.CANCELED,
]

/** "2019 Freightliner Cascadia · VIN 3AKJ…" for a vehicle, the name and state for a driver. */
export function unitLabel(unit: PolicyChangeUnit | null): string {
  if (!unit) return ''
  if (unit.type === 'driver') {
    return [`${unit.firstName ?? ''} ${unit.lastName ?? ''}`.trim(), unit.licenseState]
      .filter(Boolean)
      .join(' · ')
  }
  const vehicle = [unit.year, unit.make, unit.model].filter(Boolean).join(' ')
  return [vehicle, unit.vin && `VIN ${unit.vin}`].filter(Boolean).join(' · ')
}

const EDIT_FIELD_LABELS: Record<string, string> = {
  vin: 'VIN',
  year: 'Year',
  make: 'Make',
  model: 'Model',
  vehicle_number: 'Unit number',
  vehicle_class: 'Vehicle class',
  weight_class: 'Weight class',
  license_plate: 'License plate',
  license_state: 'State',
  price: 'Value',
  first_name: 'First name',
  last_name: 'Last name',
  license_number: 'License number',
  dob: 'Date of birth',
  date_of_hire: 'Hire date',
  cdl_date: 'CDL date',
  years_of_experience: 'Years of experience',
  violations_count: 'Violations',
  accidents_count: 'Accidents',
  suspensions_count: 'Suspensions',
}
const DATE_FIELDS = new Set(['dob', 'date_of_hire', 'cdl_date'])

function editValue(field: string, value: PolicyChangeUnitEdit['before']): string {
  if (value === null || value === '') return 'not set'
  if (field === 'price') return formatCurrency(Number(value))
  if (DATE_FIELDS.has(field)) return formatDate(String(value))
  return String(value)
}

/** "License plate: 3F25DET002 → TX 7HD-2291" for one field an edit changes. */
export function editLabel(edit: PolicyChangeUnitEdit): string {
  const label = EDIT_FIELD_LABELS[edit.field] ?? edit.field
  return `${label}: ${editValue(edit.field, edit.before)} → ${editValue(edit.field, edit.after)}`
}

/** "2 trucks · 1 driver" for a request's changes. */
export function changeCountsLabel(counts: PolicyChangeRow['changeCounts']): string {
  const parts = [
    [counts.trucks, 'truck'],
    [counts.trailers, 'trailer'],
    [counts.drivers, 'driver'],
    [counts.other, 'other change'],
  ] as const
  return (
    parts
      .filter(([count]) => count > 0)
      .map(([count, noun]) => `${count} ${noun}${count === 1 ? '' : 's'}`)
      .join(' · ') || 'No changes yet'
  )
}

/** A date input's value as the API's YYYY-MM-DD, read in local time. */
export function toIsoDate(date: Date | undefined): string | null {
  if (!date) return null
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}
